import React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { Row, Col, CardHeader, Card, CardBody } from 'reactstrap';
import { Link, withRouter, useParams } from 'react-router-dom';
import {
  performanceData,
  evaluationData,
  evaluationColumns,
  checklistData,
  attendanceData,
} from '../data';
import { newFetch, POST, GET } from '../../../../helpers/fetch';
import AssociateActivity from './AssociateActivity';
import AssociateNameScore from './AssociateNameScore';
import AssociateEvaluationForm from './AssociateEvaluationForm';
import placeholderImage from '../../../../assets/images/placeholder.jpg';
import './styles.css';

const ManagePerformance = ({ history }) => {
  const { associate_id } = useParams();
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = React.useState([
    { field: 'createdAt', sort: 'desc' },
  ]);
  const [selectedAssociatePerformance, setSelectedAssociatePerformance] =
    React.useState({});
  const [evaluation, setEvaluation] = React.useState([]);
  const [checklistSourceData, setChecklistSourceData] = React.useState([]);
  const [attendanceSourceData, setAttendanceSourceData] = React.useState([]);
  const [pickRateSourceData, setPickRateSourceData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [totalRows, setTotalRows] = React.useState(0);
  const [data, setData] = React.useState([]);
  const columns = evaluationColumns;

  React.useLayoutEffect(() => {
    fetchSelectedAssociate();
    fetchChecklist();
    fetchAttendance();
    fetchPickRates();
  }, []);

  React.useLayoutEffect(() => {
    if (evaluation !== undefined) {
      setData(evaluation);
      setLoading(false);
    }
  }, [evaluation]);

  React.useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    fetchEvaluation(paginationModel, field, sort);
  }, [sortModel, paginationModel]);

  const fetchEvaluation = async (pagination, field, sort) => {
    setLoading(true);
    let api_path = `/performance_management/view-all-associate-evaluations`;
    let response = await newFetch(POST, api_path, {
      field,
      sort,
      page: pagination.page,
      pageSize: pagination.pageSize,
      associate_id,
    });

    if (response.success) {
      const evaluationData = response?.data.list || [];
      setEvaluation(evaluationData);

      const totalRecord = response?.data.total_record || 0;
      setTotalRows(totalRecord);
    }

    setLoading(false);
  };

  const fetchSelectedAssociate = async () => {
    let api_path = `/performance_management/view-associate/${associate_id}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      setSelectedAssociatePerformance(response?.data || []);
    }
  };

  const fetchChecklist = async () => {
    let api_path = `/performance_management/view-associate-checklist/${associate_id}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      const checklist = response.data;
      setChecklistSourceData(checklist);
    }
  };

  const fetchAttendance = async () => {
    let api_path = `/performance_management/view-associate-attendance/${associate_id}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      const attendance = response.data;
      setAttendanceSourceData(attendance);
    }
  };

  const fetchPickRates = async () => {
    let api_path = `/performance_management/view-associate-pick-rate/${associate_id}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      const pickRates = response.data;
      setPickRateSourceData(pickRates);
    }
  };

  const restartFetchData = async () => {
    const { field, sort } = sortModel[0] || {};
    fetchEvaluation(paginationModel, field, sort);
    fetchSelectedAssociate();
  };

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length > 0) {
      setSortModel(newSortModel);
    }
  };

  return (
    <div
      className="container-fluid"
      style={{
        paddingLeft: '2rem',
        paddingRight: '2rem',
        paddingBottom: '6rem',
      }}
    >
      <div className="pb-2 py-2 d-flex align-items-center">
        <Link to={'/performance-management'} className="title">
          <h6 className="flex-grow-1 pt-4 text-brand">
            Back to Performance Management
          </h6>
        </Link>
      </div>

      <Row className="my-4">
        <Col lg={7} md={12} sm={12} className="container-manage-one">
          <Row>
            <Col className="d-flex flex-row">
              <AssociateNameScore
                selectedAssociatePerformance={selectedAssociatePerformance}
                placeholderImage={placeholderImage}
              />
            </Col>
          </Row>

          <AssociateEvaluationForm
            history={history}
            associate_id={associate_id}
            site_id={selectedAssociatePerformance.site_id}
            restartFetchData={restartFetchData}
          />

          <Row className="mt-5">
            <Col>
              <Card className="card-h-100 rounded-2 mt-4">
                <CardHeader className="my-2">
                  <h6 className="card-title mb-1">Evaluations</h6>
                  <p className="text-muted">
                    Below is the list of evaluations for{' '}
                    {selectedAssociatePerformance?.associate_name ? (
                      <code>
                        {selectedAssociatePerformance?.associate_name}
                      </code>
                    ) : (
                      'this associate'
                    )}
                    .
                  </p>
                </CardHeader>
                <CardBody>
                  <Box sx={{ width: '100%', fontSize: 16 }} className="mt-3">
                    <DataGrid
                      columns={columns}
                      rows={data}
                      rowCount={totalRows}
                      loading={loading}
                      pageSizeOptions={[10, 20, 30, 40, 50, 80, 100]}
                      paginationModel={paginationModel}
                      paginationMode="server"
                      onPaginationModelChange={setPaginationModel}
                      localeText={{
                        noRowsLabel: 'No evaluation found.',
                      }}
                      initialState={{
                        pagination: {
                          paginationModel: { page: 0, pageSize: 10 },
                        },
                      }}
                      style={{ minHeight: 100 }}
                      sortModel={sortModel}
                      onSortModelChange={handleSortModelChange}
                      getRowHeight={() => 'auto'}
                      sx={{
                        [`& .${gridClasses.cell}`]: {
                          py: 1,
                        },
                        [`& .${gridClasses.columnHeaderTitle}`]: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#6c757d',
                        },
                      }}
                      disableSelectionOnClick
                      isRowSelectable={() => false}
                    />
                  </Box>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col lg={5} md={12} sm={12} className="container-manage-two">
          <AssociateActivity
            checklistData={checklistSourceData}
            attendanceData={attendanceSourceData}
            pickRatesData={pickRateSourceData}
          />
        </Col>
      </Row>
    </div>
  );
};

export default withRouter(ManagePerformance);
